import SWSLogoBlack from '@assets/images/logo/sws-logo-black.svg';
import SWSLogo from '@assets/images/logo/sws-logo-updated.svg';
import DownloadAppStore from '@assets/images/icons/vendor/download-on-the-appstore.svg';
import DownloadGooglePlay from '@assets/images/icons/vendor/get-it-on-google-play.svg';
import DownloadChromeExtension from '@assets/images/icons/vendor/download-chrome-extension.svg';
import LinkedInIcon from '@assets/images/logo/linkedin.svg';
import XIcon from '@assets/images/logo/x.svg';
import YouTubeIcon from '@assets/images/logo/youtube-logo.svg';
import SpotifyIcon from '@assets/images/logo/spotify.svg';
import ApplePodcastsIcon from '@assets/images/logo/apple-podcasts.svg';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import {
  ROUTE_COMPANY,
  ROUTE_CREATE_CRITERIA,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_GIFT_CARD_REDEEM,
  ROUTE_INVESTING_IDEA_HOME,
  ROUTE_LOCALE_COMPANY,
  ROUTE_MARKET,
  ROUTE_MARKET_HUB,
  ROUTE_NARRATIVE_COMMUNITY,
  ROUTE_PLANS,
  ROUTE_STOCK_PAGE,
  URL_SALES_SITE,
  URL_SALES_SITE_PLAIN,
} from '@/constants/routes';
import { useLocation } from '@/hooks/useLocation';
import { cn, Link } from '@simplywallst/ui-core/next';
import { useTranslation } from '@/hooks/useTranslation';
import useUser from '@/hooks/useUser';
import DocumentSchema from '@/components/DocumentSchema';
import type { Dataset } from 'schema-dts';
import { getPath } from '@/router/utils';
import { PUBLIC_SWS_ENV } from '@/constants/env';

const getSalesSiteUrl = (path: string) =>
  PUBLIC_SWS_ENV === 'prod' ? path : `${URL_SALES_SITE_PLAIN}${path}`;

const Schema = () => {
  const t = useTranslation();
  return (
    <DocumentSchema<Dataset>
      item={{
        '@context': 'https://schema.org',
        '@type': 'Dataset',
        name: t('page.schema.dataSet.name'),
        description: t('page.schema.dataSet.description'),
        creator: {
          '@type': 'Organization',
          name: 'Simply Wall St Pty Ltd',
          url: 'https://simplywall.st',
        },
        isBasedOn: {
          '@type': 'Dataset',
          name: 'S&P Global Market Intelligence',
          description: t('page.schema.dataSet.s&p.description'),
          provider: {
            '@type': 'Organization',
            name: 'S&P Global',
            url: 'https://www.spglobal.com',
          },
        },
        license:
          'https://www.spglobal.com/content/dam/spglobal/corporate/en/documents/secure/MI-FastTrack-Terms-and-Conditions-July-2023.pdf',
      }}
    />
  );
};

const LogoSection = ({ isPrintMode = false }: { isPrintMode?: boolean }) => {
  const t = useTranslation();

  const snpGlobalLogo = useCloudflareImage({
    src: 'https://cdn.prod.website-files.com/648009cb318551f1806b36b4/6487b1b44ebc33f108273875_sp-global-logo.png',
  });
  const snpGlobalLogoLight = useCloudflareImage({
    src: 'https://simplywall.st/static/images/sp-global-logo-1.png',
  });

  return (
    <div className="grid gap-x-4 gap-y-10 text-xs text-softer @[810px]:grid-cols-2 @[810px]:gap-x-8 @[1024px]:gap-y-3.5 [&_a]:text-solid">
      <div className="grid gap-y-5">
        <div className="grid gap-1.5">
          <div className="max-w-[24ch]">
            <a
              href={URL_SALES_SITE}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="block"
            >
              {isPrintMode ? <SWSLogoBlack /> : <SWSLogo />}
            </a>
          </div>
          <address className="not-italic">
            Level 5, 320 Pitt Street, Sydney
          </address>
        </div>

        {!isPrintMode && (
          <>
            <ul className="m-0 ml-[-.75ch] grid max-w-[26ch] grid-flow-col items-center">
              <li>
                <a
                  href="https://www.linkedin.com/company/simply-wall-st"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="block"
                >
                  <LinkedInIcon aria-label="LinkedIn" className="h-5 w-full" />
                </a>
              </li>
              <li>
                <a
                  href="https://twitter.com/simplywallst"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="block"
                >
                  <XIcon
                    aria-label="Twitter"
                    className="h-[23px] w-full rounded"
                  />
                </a>
              </li>
              <li className="ml-3.5">
                <a
                  href="https://www.youtube.com/c/Simplywallst"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="-mx-0.5 block"
                >
                  <YouTubeIcon aria-label="YouTube" className="h-6 w-full" />
                </a>
              </li>
              <li>
                <a
                  href="https://open.spotify.com/show/76l4P66AfdiOqFmJ5II2AO"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="block"
                >
                  <SpotifyIcon
                    aria-label="Spotify Podcast"
                    className="h-5 w-full"
                  />
                </a>
              </li>
              <li>
                <a
                  href="https://podcasts.apple.com/au/podcast/simply-wall-st/id1695670179"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  className="block"
                >
                  <ApplePodcastsIcon
                    aria-label="Apple Podcast"
                    className="h-5 w-full"
                  />
                </a>
              </li>
            </ul>

            <ul className="m-0 flex flex-wrap gap-2">
              <li>
                <a
                  href="https://apps.apple.com/app/apple-store/id1075614972?pt=117976858&ct=website-footer&mt=8"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="block"
                >
                  <DownloadAppStore aria-label="Download on the App Store" />
                </a>
              </li>
              <li>
                <a
                  href="https://play.google.com/store/apps/details?id=com.simplywallst.app&referrer=utm_source%3Dwebsite%26utm_medium%3Dfooter%26utm_content%3Dgoogle-play-logo"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="block"
                >
                  <DownloadGooglePlay aria-label="Get it on Google Play" />
                </a>
              </li>
              <li>
                <a
                  href="https://chromewebstore.google.com/detail/stock-analysis-overlay-si/fogdlgjnkolhbefbbpghbafdkhcdgdnk"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="block"
                >
                  <DownloadChromeExtension aria-label="Download free extension on Chrome Web Store" />
                </a>
              </li>
            </ul>
          </>
        )}
      </div>

      <div className="grid gap-y-3.5 @[810px]:mt-5">
        <a
          href="https://spglobal.com/market-intelligence/en"
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="block justify-self-start"
        >
          <img
            src={isPrintMode ? snpGlobalLogoLight : snpGlobalLogo}
            width="170"
            height="46"
            alt="S&P Global Market Intelligence"
            loading="lazy"
            className="block"
          />
        </a>
        <div>
          Financial Data provided by S&P Global Market Intelligence LLC,
          analysis provided by Simply Wall Street Pty Ltd. Copyright &copy;{' '}
          {new Date().getFullYear()}, S&P Global Market Intelligence LLC.{' '}
          <span className="whitespace-nowrap">All rights reserved.</span>
        </div>
        {!isPrintMode && (
          <a href="https://simplywall.st/analysis-and-financial-data-sources">
            {t('app.footer.links.viewDataSources')}
          </a>
        )}
      </div>
    </div>
  );
};

const LinksSection = () => {
  const t = useTranslation();
  const { data: userData } = useUser();

  return (
    <div
      className={cn(
        // Container queries allow footer placement in small-width layouts.
        // For example, on the markets page it's within the content container.
        '-mt-7 gap-x-4 @[570px]:columns-3 @[810px]:columns-4 @[810px]:gap-x-8 @[1024px]:mt-0 @[1024px]:grid @[1024px]:grid-cols-6 @[1024px]:[&_h5]:mt-0',
        ' columns-2 items-start text-xs',
        '[&>*]:[break-inside:avoid] [&_ul]:overflow-hidden',
        '[&_h5]:m-0 [&_h5]:mt-7 [&_h5]:truncate [&_h5]:font-normal [&_h5]:text-softer [&_h5]:[font-size:inherit]',
        '[&_a]:block [&_a]:truncate [&_a]:py-1.5 [&_a]:text-solid [&_a]:[font-size:inherit]'
      )}
    >
      <div className="grid gap-4">
        <h5>{t('app.footer.labels.markets')}</h5>
        <ul>
          {[
            ['us', 'US: NYSE & NASDAQ'],
            ['gb', 'UK: FTSE'],
            ['au', 'Australia: ASX'],
            ['in', 'India: NIFTY'],
            ['ca', 'Canada: TSX'],
            ['za', 'South Africa: JSE'],
            ['jp', 'Japan: NIKKEI'],
            ['kr', 'South Korea: KOSPI'],
            ['de', 'Germany: DAX'],
          ].map(([market, label]) => (
            <li key={market}>
              <Link href={getPath({ type: ROUTE_MARKET, payload: { market } })}>
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-4">
        <h5>{t('app.footer.labels.investingIdeas')}</h5>
        <ul>
          {/* Note: These links don't point to the right resource outside prod */}
          {[
            [
              168,
              'undervalued-stocks-based-on-cash-flows',
              t('app.footer.idealinks.undervalued'),
            ],
            [
              146,
              'dividend-powerhouses-3-yield',
              t('app.footer.idealinks.dividendPowerhouses'),
            ],
            [171, 'insider-trading', t('app.footer.idealinks.insiderBuying')],
            [
              215173,
              'nuclear-energy-stocks',
              t('app.footer.idealinks.nuclearEnergy'),
            ],
            [
              5620,
              'electric-autonomous-vehicle-stocks',
              t('app.footer.idealinks.autonomousVehicle'),
            ],
            [
              215169,
              'artificial-intelligence-ai-stocks',
              t('app.footer.idealinks.artificialIntelligence'),
            ],
            [
              16734,
              'cryptocurrency-and-blockchain-stocks',
              t('app.footer.idealinks.cryptoBlockchain'),
            ],
            [10184, 'cybersecurity', t('app.footer.idealinks.cybersecurity')],
          ].map(([id, name, label]) => (
            <li key={id}>
              <Link
                href={getPath({
                  type: ROUTE_INVESTING_IDEA_HOME,
                  payload: { id, name },
                })}
              >
                {label}
              </Link>
            </li>
          ))}
          <li>
            <Link
              href={getPath({
                type: ROUTE_DISCOVER_INVESTING_IDEAS,
                payload: {},
              })}
            >
              {t('app.footer.links.moreIdeas')}
            </Link>
          </li>
        </ul>
      </div>

      <div className="grid gap-4">
        <h5>{t('app.footer.links.stockCommunities')}</h5>
        <ul>
          {[
            // Older links
            // ['tsx', 'enb', 'Enbridge'],
            // ['tsx', 'trp', 'TC Energy'],
            // ['tsx', 'ry', 'Royal Bank of Canada'],
            // ['tsx', 'bmo', 'Bank of Montreal'],
            // ['tsx', 'wsp', 'WSP Global'],
            // ['tsx', 'stn', 'Stantec'],
            // ['tsx', 'cnq', 'Canadian Natural Resources'],
            // ['tsx', 'su', 'Suncor Energy Stock Price'],
            // ['tsx', 'mfc', 'Manulife Financial'],
            // ['tsx', 'cm', 'Canadian Imperial Bank of Commerce'],
            // ['tsx', 'td', 'Toronto-Dominion Bank'],
            // ['tsx', 'bns', 'Bank of Nova Scotia'],
            // ['tsx', 'ppl', 'Pembina Pipeline'],
            // ['tsx', 'na', 'National Bank of Canada'],
            // ['tsx', 'cve', 'Cenovus Energy'],

            // We're cycling through different links while the quote page are new
            ['lse', 'azn', 'AstraZeneca'],
            ['lse', 'hsba', 'HSBC Holdings'],
            ['lse', 'shel', 'Shell'],
            ['lse', 'ulvr', 'Unilever'],
            ['lse', 'dge', 'Diageo'],
            ['lse', 'rio', 'Rio Tinto Group'],
            ['lse', 'rel', 'RELX'],
            ['lse', 'bp.', 'BP'],
            ['lse', 'barc', 'Barclays'],
          ].map(([exchange, ticker, label]) => (
            <li key={`${exchange}-${ticker}`}>
              <Link
                href={getPath({
                  type: ROUTE_STOCK_PAGE,
                  payload: { exchange, ticker },
                })}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-4">
        <h5>{t('app.footer.labels.featuresTools')}</h5>
        <ul>
          <li>
            <a href={getSalesSiteUrl('/features/portfolio')}>
              {t('app.footer.links.portfolioTracker')}
            </a>
          </li>
          <li>
            <Link href={getPath({ type: ROUTE_CREATE_CRITERIA, payload: {} })}>
              {t('app.footer.links.screenerAlerts')}
            </Link>
          </li>
          <li>
            <Link
              href={getPath({ type: ROUTE_NARRATIVE_COMMUNITY, payload: {} })}
            >
              {t('app.footer.labels.narrativesFairValues')}
            </Link>
          </li>
          <li>
            <a href="https://simplywall.st/tools/dividend-calculator">
              {t('app.footer.links.dividendCalculator')}
            </a>
          </li>
        </ul>
      </div>

      <div className="grid gap-4">
        <h5>{t('app.footer.labels.newsDiscovery')}</h5>
        <ul>
          <li>
            <Link href={`${URL_SALES_SITE_PLAIN}/news`}>
              {t('app.footer.links.news')}
            </Link>
          </li>
          <li>
            <Link href={getPath({ type: ROUTE_MARKET_HUB, payload: {} })}>
              {t('app.footer.links.globalInsights')}
            </Link>
          </li>
          <li>
            <Link
              href={getPath({
                type: ROUTE_DISCOVER_INVESTING_IDEAS,
                payload: {},
              })}
            >
              {t('app.footer.links.investingIdeas')}
            </Link>
          </li>
          <li>
            <Link
              href={getPath({ type: ROUTE_NARRATIVE_COMMUNITY, payload: {} })}
            >
              {t('app.footer.links.communityNarratives')}
            </Link>
          </li>
          <li>
            <a href="https://support.simplywall.st/hc/en-us/articles/7894830045199-Changelog">
              {t('app.footer.links.whatsNew')}
            </a>
          </li>
        </ul>
      </div>

      <div className="grid gap-4">
        <div className="grid gap-4">
          <h5>Simply Wall St</h5>
          <ul>
            <li>
              <Link href={getPath({ type: ROUTE_PLANS, payload: {} })}>
                {t('app.footer.links.plans')}
              </Link>
            </li>
            {userData.loggedIn && (
              <li>
                <Link
                  href={getPath({ type: ROUTE_GIFT_CARD_REDEEM, payload: {} })}
                >
                  {t('app.footer.links.redeemGiftCard')}
                </Link>
              </li>
            )}
            <li>
              <a href={getSalesSiteUrl('/about')}>
                {t('app.footer.links.aboutUs')}
              </a>
            </li>
            <li>
              <a href={getSalesSiteUrl('/our-people')}>
                {t('app.footer.links.ourPeople')}
              </a>
            </li>
            <li>
              <a href="https://support.simplywall.st/hc/en-us/requests/new">
                {t('app.footer.links.contactUs')}
              </a>
            </li>
            <li>
              <a href={getSalesSiteUrl('/careers')}>
                {t('app.footer.links.careers')}
              </a>
            </li>
          </ul>
        </div>
        <div>
          <ul>
            <li>
              <a href="https://support.simplywall.st/hc/en-us">
                {t('app.footer.links.help')}
              </a>
            </li>
            <li>
              <a href="https://learn.simplywall.st">
                {t('app.footer.links.learn')}
              </a>
            </li>
            <li>
              <a href={getSalesSiteUrl('/affiliate-program')}>
                {t('app.footer.links.affiliate')}
              </a>
            </li>
            <li>
              <a href={getSalesSiteUrl('/business-plans')}>
                {t('app.footer.links.business')}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const LegalSection = ({ isPrintMode = false }: { isPrintMode?: boolean }) => {
  const t = useTranslation();
  const location = useLocation();
  const showClearbit =
    location.type === ROUTE_COMPANY || location.type === ROUTE_LOCALE_COMPANY;

  return (
    <div className="grid items-start gap-x-inherit gap-y-2.5 text-xs leading-normal text-subtle @[1024px]:grid-cols-6 light:text-softer">
      <div className="text-pretty @[1024px]:col-span-4">
        Simply Wall Street Pty Ltd (ACN 600 056 611), is a Corporate Authorised
        Representative (Authorised Representative Number: 467183) of Sanlam
        Private Wealth Pty Ltd (AFSL No. 337927). Any advice contained in this
        website is general advice only and has been prepared without considering
        your objectives, financial situation or needs. You should not rely on
        any advice and/or information contained in this website and before
        making any investment decision we recommend that you consider whether it
        is appropriate for your situation and seek appropriate financial,
        taxation and legal advice. Please read our Financial Services Guide
        before deciding whether to obtain financial services from us.
      </div>
      <div className="grid gap-2.5 @[1024px]:col-span-2">
        <div className="text-pretty">
          &copy; {new Date().getFullYear()} Simply Wall Street Pty Ltd, US
          Design Patent #29/544/281, Community and European Design Registration
          #2845206
        </div>
        {!isPrintMode && (
          <ul className="flex flex-wrap gap-inherit">
            <li>
              <a href="/terms-and-conditions">{t('app.footer.links.terms')}</a>
            </li>
            <li>
              <a href="/privacy-policy">{t('app.footer.links.privacy')}</a>
            </li>
            <li>
              <a href="https://media.simplywall.st/site/financials/Financial_Services_Guide_-_Simply_Wall_St_V2.0_dated_20_Jan_23.pdf">
                {t('app.footer.labels.featuresServicesGuide')}
              </a>
            </li>
            {showClearbit && (
              <li>
                <a
                  href="https://clearbit.com/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  {t('app.footer.links.clearbit')}
                </a>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto w-full max-w-screen-xl px-4 @container/footer lg:px-0">
    <footer
      data-cy-id="footer"
      className={cn(
        'pb-3 pt-10 @[1024px]:pb-0 @[1024px]:pt-16',
        'grid gap-x-8 gap-y-10 @[810px]:gap-y-14',
        '[&_a:focus]:underline [&_a:hover]:underline [&_a]:text-[inherit] [&_a]:no-underline [&_a]:transition-none [&_a]:[text-decoration-color:inherit] [&_svg]:block [&_ul]:[font-size:inherit] [&_ul_a]:[font-size:inherit]'
      )}
    >
      {children}
    </footer>
  </div>
);

/**
 * Page Footer
 * Container queries allow the footer to be displayed in small widths.
 *
 * Please keep the footer in-sync with the other footer locations:
 * - Deprecated Web
 * - Web
 * - News
 * - Webflow (sales site)
 */
export const Footer = () => {
  const location = useLocation();

  if (RUNTIME_ENV === 'native') return;

  if (location.query?.viewmode === 'print')
    return (
      <Wrapper>
        <LogoSection isPrintMode />
        <LegalSection isPrintMode />
      </Wrapper>
    );

  return (
    <>
      <Schema />
      <Wrapper>
        <LogoSection />
        <LinksSection />
        <LegalSection />
      </Wrapper>
    </>
  );
};
